import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as ProfitLossIcon,
  AccountBalance as BalanceSheetIcon,
  Timeline as CashFlowIcon,
  Receipt as TaxReportIcon,
  PieChart as SalesReportIcon,
  BarChart as InventoryReportIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import QuickAccessPanel from '../components/QuickAccessPanel';

const Shortcuts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const reportItems = [
    {
      icon: <ProfitLossIcon />,
      label: 'Profit & Loss',
      path: '/reports/profit-loss',
      color: '#4361ee',
    },
    {
      icon: <BalanceSheetIcon />,
      label: 'Balance Sheet',
      path: '/reports/balance-sheet',
      color: '#3f37c9',
    },
    {
      icon: <CashFlowIcon />,
      label: 'Cash Flow',
      path: '/reports/cash-flow',
      color: '#4895ef',
    },
    {
      icon: <TaxReportIcon />,
      label: 'Tax Reports',
      path: '/reports/tax',
      color: '#4cc9f0',
    },
    {
      icon: <SalesReportIcon />,
      label: 'Sales Reports',
      path: '/reports/sales',
      color: '#f72585',
    },
    {
      icon: <InventoryReportIcon />,
      label: 'Inventory Reports',
      path: '/reports/inventory',
      color: '#7209b7',
    },
  ];

  const getIconSize = () => {
    if (isMobile) return 40;
    if (isTablet) return 50;
    return 60;
  };

  const getGridSize = () => {
    if (isMobile) return 3;
    if (isTablet) return 4;
    return 3;
  };

  return (
    <Box sx={{ 
      height: '100%', 
      background: '#ffffff',
      overflow: 'auto'
    }}>
      <Container maxWidth={false} sx={{ mt: 3, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '10px',
            minHeight: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
              Quick Access
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Access your frequently used forms and features quickly
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <QuickAccessPanel />
          </Box>

          <Box sx={{ mt: 6, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Reports
              </Typography>
            </Box>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
              Access detailed financial and business reports
            </Typography>

            <Grid container spacing={2}>
              {reportItems.map((item, index) => (
                <Grid item xs={6} sm={4} md={getGridSize()} key={index}>
                  <Tooltip title={item.label} placement="top">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          transition: 'transform 0.2s',
                        },
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          width: getIconSize(),
                          height: getIconSize(),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${item.color}15`,
                          borderRadius: '12px',
                          mb: 1,
                          border: `1px solid ${item.color}30`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: `${item.color}25`,
                            boxShadow: `0 4px 12px ${item.color}30`,
                          },
                        }}
                      >
                        <IconButton
                          sx={{
                            color: item.color,
                            '&:hover': {
                              bgcolor: `${item.color}20`,
                            },
                          }}
                        >
                          {item.icon}
                        </IconButton>
                      </Paper>
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          fontWeight: 500,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Shortcuts; 